import { Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import type React from 'react'
import { isNonNil } from 'utils'

import { useLocalization } from '../features/localization'
import { SpriteMap } from '../global'
import { createVariants } from '../lib'
import { Icon } from './Icon'

interface Props {
  title?: React.ReactNode
  description?: string
  onClose?: () => void
  show: boolean
  children: React.ReactNode
  unstyled?: boolean
  size?: 'md' | 'lg' | 'xl' | 'full'
}

const dialogPanelClasses = createVariants({
  base: 'inline-block transform align-middle transition-all',
  variants: {
    size: {
      md: 'max-w-[454px]',
      lg: 'max-w-lg',
      xl: 'max-w-4xl',
      full: 'max-w-full'
    },
    unstyled: {
      false:
        'my-8 w-full rounded-2xl bg-white p-6 text-left shadow-xl group-data-[closed]:scale-95 group-data-[closed]:opacity-0'
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

/**
 *  @group UI Components
 */
export const Modal: React.FC<Props> = ({
  title,
  description = '',
  children,
  show,
  onClose,
  unstyled = false,
  size = 'md'
}) => {
  const { t } = useLocalization()

  return (
    <Dialog
      as="div"
      open={show}
      className="group fixed inset-0 z-[99] overflow-y-auto transition duration-200 data-[closed]:opacity-0"
      onClose={onClose ?? (() => {})}
      transition
    >
      <div className="min-h-screen px-4 text-center">
        <DialogBackdrop className="fixed inset-0 bg-gray-900 opacity-40 transition-opacity" />

        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <DialogPanel className={dialogPanelClasses({ size, unstyled })}>
          {unstyled ? (
            children
          ) : (
            <>
              {(isNonNil(title) || isNonNil(onClose)) && (
                <div className="flex items-center justify-between gap-4">
                  {isNonNil(title) && (
                    <DialogTitle as="h3" className="pan-title4 flex items-center gap-3">
                      {title}
                    </DialogTitle>
                  )}
                  {isNonNil(onClose) && (
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label={t(($) => $.buttons.close)}
                      className="ml-auto shrink-0 text-gray-600 hover:text-gray-900"
                    >
                      <Icon icon={SpriteMap.CloseIcon} className="size-3" />
                    </button>
                  )}
                </div>
              )}
              {description.length > 0 && (
                <Description className="pan-body my-6 text-gray-900">{description}</Description>
              )}
              {children}
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
